import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  Building2,
  Check,
  Plus,
  Settings,
  Search,
  Shield,
} from "lucide-react";
import { useLocation, Location } from "@/contexts/LocationContext";
import { useAuth } from "@/contexts/AuthContext";

interface LocationSwitcherProps {
  isCollapsed?: boolean;
}

interface OrgGroup {
  orgId: string;
  orgName: string;
  locations: Location[];
  /** En qué cajón cae: quién entra, quién no y quién no entró nunca. */
  actividad: Actividad;
}

/**
 * Agrupación por ACTIVIDAD REAL del superadmin (19-sep-2026).
 *
 * Con datos de producción de ese día: 38 organizaciones en el desplegable y
 * solo 7 habían entrado en los últimos 30 días; 22 llevaban más de un mes sin
 * aparecer y 9 no habían entrado nunca. Buscar entre 32 para usar 7 convertía
 * el selector en un cajón de sastre.
 *
 * OJO: "activo" aquí es ENTRAR EN LA CUENTA, no el estado de la suscripción.
 * 31 de esas 38 figuraban como suscripción "active" y aun así no entraba
 * nadie: mezclar ambas cosas es justo lo que hacía inútil la lista.
 */
type Actividad = "activos" | "dormidos" | "nunca";

/** Un mes sin entrar: si no han vuelto en 30 días, no es a quien buscas. */
const DIAS_PARA_DORMIDO = 30;

const TITULOS: Record<Actividad, string> = {
  activos: "Activos",
  dormidos: "Sin actividad",
  nunca: "Nunca han entrado",
};

function clasificarActividad(loc: Location): Actividad {
  const ultimo = loc.organization?.users?.[0]?.lastLoginAt;
  if (!ultimo) return "nunca";
  const dias = (Date.now() - new Date(ultimo).getTime()) / 86400000;
  return dias <= DIAS_PARA_DORMIDO ? "activos" : "dormidos";
}

export default function LocationSwitcher({
  isCollapsed = false,
}: LocationSwitcherProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    locations,
    currentLocation,
    setCurrentLocation,
    canCreateLocation,
    maxLocations,
  } = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const isSuperAdmin = user?.role === "owner";

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens (superadmin only)
  useEffect(() => {
    if (isOpen && isSuperAdmin && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen, isSuperAdmin]);

  // Group locations by organization (for superadmin)
  const orgGroups: OrgGroup[] = useMemo(() => {
    if (!isSuperAdmin) return [];

    const groups = new Map<string, OrgGroup>();
    const query = searchQuery.toLowerCase().trim();

    const INACTIVE_STATUSES = ["suspended", "cancelled"];

    for (const loc of locations) {
      const orgId = loc.organization?.id || loc.organizationId || "unknown";
      const orgName = loc.organization?.name || "Sin organización";

      // Skip inactive organizations
      if (
        loc.organization?.subscriptionStatus &&
        INACTIVE_STATUSES.includes(loc.organization.subscriptionStatus)
      ) {
        continue;
      }

      // Apply search filter
      if (query) {
        const matchesLocation =
          loc.name.toLowerCase().includes(query) ||
          loc.city.toLowerCase().includes(query) ||
          loc.address.toLowerCase().includes(query);
        const matchesOrg = orgName.toLowerCase().includes(query);
        if (!matchesLocation && !matchesOrg) continue;
      }

      if (!groups.has(orgId)) {
        groups.set(orgId, { orgId, orgName, locations: [], actividad: clasificarActividad(loc) });
      }
      groups.get(orgId)!.locations.push(loc);
    }

    return Array.from(groups.values());
  }, [locations, isSuperAdmin, searchQuery]);

  // Reparto en las tres secciones, respetando el orden alfabético que ya traía.
  const secciones = useMemo(() => {
    const vacio: Record<Actividad, OrgGroup[]> = { activos: [], dormidos: [], nunca: [] };
    for (const g of orgGroups) vacio[g.actividad].push(g);
    return vacio;
  }, [orgGroups]);

  // Plegado/desplegado. "Activos" abierto; los otros dos cerrados, que es el
  // motivo de todo esto. Al BUSCAR se abren los tres: un buscador que no
  // encuentra lo que existe es peor que no agrupar.
  const [abiertas, setAbiertas] = useState<Record<Actividad, boolean>>({
    activos: true,
    dormidos: false,
    nunca: false,
  });
  const buscando = searchQuery.trim().length > 0;
  const estaAbierta = (a: Actividad): boolean => buscando || abiertas[a];

  // Filtered locations for non-superadmin
  const filteredLocations = useMemo(() => {
    if (isSuperAdmin) return locations;
    return locations;
  }, [locations, isSuperAdmin]);

  // Current org name for display
  const currentOrgName = isSuperAdmin
    ? currentLocation?.organization?.name
    : undefined;

  // Don't show if no locations
  if (locations.length === 0) {
    return null;
  }

  const handleSelect = (location: Location) => {
    const isChanging = currentLocation?.id !== location.id;
    setCurrentLocation(location);
    setIsOpen(false);
    setSearchQuery("");
    if (isChanging) {
      window.location.reload();
    }
  };

  const handleManageLocations = () => {
    setIsOpen(false);
    setSearchQuery("");
    navigate("/locations");
  };

  const renderDropdownContent = () => (
    <>
      {/* Header */}
      <div className="px-3 py-2 border-b border-gray-100">
        {isSuperAdmin ? (
          <div className="flex items-center gap-1.5">
            <Shield size={12} className="text-amber-500" />
            <p className="text-xs font-medium text-amber-600 uppercase tracking-wider">
              Todas las ubicaciones (
              {orgGroups.reduce((sum, g) => sum + g.locations.length, 0)})
            </p>
          </div>
        ) : (
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            Tus locales ({locations.length}/{maxLocations})
          </p>
        )}
      </div>

      {/* Search bar for superadmin */}
      {isSuperAdmin && (
        <div className="px-3 py-2 border-b border-gray-100">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar restaurante u organización..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      )}

      {/* Location list */}
      <div className="max-h-80 overflow-y-auto overflow-x-hidden py-1">
        {isSuperAdmin ? (
          // Superadmin: grouped by organization
          orgGroups.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-gray-500">
              No se encontraron ubicaciones
            </div>
          ) : (
            <>
            {/* El negocio en el que estás, SIEMPRE visible y arriba del todo:
                si cae en una sección plegada (te pasa con los dormidos), no
                perderías la referencia de dónde estás parado. Y no se abre la
                sección entera por ello, que son 22 entradas. */}
            {currentLocation && !buscando && (
              <div className="px-3 py-2 flex items-center gap-3 bg-primary-50 border-b border-primary-100">
                <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <Building2 size={14} className="text-primary-600" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-primary-700 truncate">
                    {currentLocation.name}
                  </p>
                  <p className="text-xs text-primary-600/70 truncate">Estás aquí</p>
                </div>
                <Check size={16} className="text-primary-600 flex-shrink-0" />
              </div>
            )}
            {(["activos", "dormidos", "nunca"] as Actividad[]).map((seccion) => {
              const grupos = secciones[seccion];
              if (grupos.length === 0) return null;
              const negocios = grupos.reduce((n, g) => n + g.locations.length, 0);
              const desplegada = estaAbierta(seccion);
              return (
                <div key={seccion}>
                  <button
                    type="button"
                    onClick={() =>
                      setAbiertas((prev) => ({ ...prev, [seccion]: !prev[seccion] }))
                    }
                    className="w-full px-3 py-2 flex items-center justify-between bg-gray-100 hover:bg-gray-200 transition-colors sticky top-0 z-10"
                  >
                    <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">
                      {TITULOS[seccion]} ({negocios})
                    </span>
                    <ChevronDown
                      size={14}
                      className={`text-gray-500 transition-transform ${desplegada ? "rotate-180" : ""}`}
                    />
                  </button>
                  {desplegada &&
                    grupos.map((group) => (
              <div key={group.orgId}>
                <div className="px-3 py-1.5 bg-gray-50 sticky top-0">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider truncate">
                    {group.orgName}
                  </p>
                </div>
                {group.locations.map((location) => (
                  <button
                    key={location.id}
                    onClick={() => handleSelect(location)}
                    className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                      currentLocation?.id === location.id ? "bg-primary-50" : ""
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        currentLocation?.id === location.id
                          ? "bg-primary-100"
                          : "bg-gray-100"
                      }`}
                    >
                      <Building2
                        size={14}
                        className={
                          currentLocation?.id === location.id
                            ? "text-primary-600"
                            : "text-gray-500"
                        }
                      />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p
                        className={`text-sm font-medium truncate ${
                          currentLocation?.id === location.id
                            ? "text-primary-700"
                            : "text-gray-900"
                        }`}
                      >
                        {location.name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {location.city || location.address || "Sin ciudad"}
                      </p>
                    </div>
                    {currentLocation?.id === location.id && (
                      <Check
                        size={16}
                        className="text-primary-600 flex-shrink-0"
                      />
                    )}
                  </button>
                ))}
              </div>
                    ))}
                </div>
              );
            })}
            </>
          )
        ) : (
          // Regular user: flat list
          filteredLocations.map((location) => (
            <button
              key={location.id}
              onClick={() => handleSelect(location)}
              className={`w-full px-3 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                currentLocation?.id === location.id ? "bg-primary-50" : ""
              }`}
            >
              <div
                className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${
                  currentLocation?.id === location.id
                    ? "bg-primary-100"
                    : "bg-gray-100"
                }`}
              >
                <Building2
                  size={16}
                  className={
                    currentLocation?.id === location.id
                      ? "text-primary-600"
                      : "text-gray-500"
                  }
                />
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p
                  className={`text-sm font-medium truncate ${
                    currentLocation?.id === location.id
                      ? "text-primary-700"
                      : "text-gray-900"
                  }`}
                >
                  {location.name}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {location.address}, {location.city}
                </p>
              </div>
              {currentLocation?.id === location.id && (
                <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center">
                  <Check size={14} className="text-primary-600" />
                </div>
              )}
            </button>
          ))
        )}
      </div>

      {/* Footer actions */}
      {!isSuperAdmin && (
        <div className="border-t border-gray-100 pt-1 mt-1">
          {canCreateLocation && (
            <button
              onClick={handleManageLocations}
              className="w-full px-3 py-2.5 flex items-center gap-3 text-primary-600 hover:bg-primary-50 transition-colors"
            >
              <div className="w-9 h-9 rounded-xl bg-primary-50 flex items-center justify-center">
                <Plus size={16} />
              </div>
              <span className="text-sm font-medium">
                Añadir nuevo restaurante
              </span>
            </button>
          )}
          <button
            onClick={handleManageLocations}
            className="w-full px-3 py-2.5 flex items-center gap-3 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center">
              <Settings size={16} />
            </div>
            <span className="text-sm">Gestionar ubicaciones</span>
          </button>
        </div>
      )}
    </>
  );

  // Collapsed view - just show icon with tooltip
  if (isCollapsed) {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full p-2 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          title={currentLocation?.name || "Seleccionar restaurante"}
        >
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              isSuperAdmin ? "bg-amber-100" : "bg-primary-100"
            }`}
          >
            {isSuperAdmin ? (
              <Shield size={16} className="text-amber-600" />
            ) : (
              <Building2 size={16} className="text-primary-600" />
            )}
          </div>
        </button>

        {/* Dropdown for collapsed */}
        {isOpen && (
          <div className="absolute left-full top-0 ml-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
            {renderDropdownContent()}
          </div>
        )}
      </div>
    );
  }

  // Expanded view
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 py-2.5 flex items-center gap-3 rounded-xl transition-all group ${
          isSuperAdmin
            ? "bg-amber-50 hover:bg-amber-100 border border-amber-200"
            : "bg-gray-50 hover:bg-gray-100"
        }`}
      >
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
            isSuperAdmin
              ? "bg-amber-100 group-hover:bg-amber-200"
              : "bg-primary-100 group-hover:bg-primary-200"
          }`}
        >
          {isSuperAdmin ? (
            <Shield size={20} className="text-amber-600" />
          ) : (
            <Building2 size={20} className="text-primary-600" />
          )}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {currentLocation?.name || "Seleccionar"}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {isSuperAdmin
              ? currentLocation?.city || currentOrgName || "restaurante"
              : currentLocation?.city || "restaurante"}
          </p>
        </div>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200 overflow-hidden">
          {renderDropdownContent()}
        </div>
      )}
    </div>
  );
}
